/* eslint-disable jsx-a11y/anchor-is-valid */

import { useModal } from '@components/Modals/useModal';
import useFormatDateText from '@hooks/useFormatDateText';
import useIsCustomerInStore from '@hooks/useIsCustomerInStore';
import ellipsisIcon from '@ingka/ssr-icon/paths/ellipses-vertical';
import Text from '@skapa/Text';
import { SourceList } from 'apis/types';
import useTranslations from 'context/Translations';
import React from 'react';
import Button from 'skapa/Button';
import styles from './ListThumbnail.module.scss';
import ListThumbnailImages from './ListThumbnailImages';

type ListThumbnailProps = {
    list: SourceList;
};
const ListThumbnail: React.FC<ListThumbnailProps> = ({ list }) => {
    const { showModal } = useModal();
    const isCustomerInStore = useIsCustomerInStore();
    const translate = useTranslations();
    const formatDateText = useFormatDateText();
    const dateText = formatDateText(list.updated);

    const linkHref = isCustomerInStore
        ? `${list.listId}?view=store`
        : `${list.listId}?view=retail`;
    const itemImages = list.items.map(item => ({
        images: item.product?.images || [],
        alt: item.product?.name || '',
    }));

    const designImages = list.designs.map(design => ({
        images: design.images || [],
        alt: design.configurationId || '',
    }));

    const numberOfItems =
        list.items.length === 0
            ? translate('LISTOVERVIEW_LISTS_THUMBNAIL_EMPTY')
            : translate('LISTOVERVIEW_LISTS_THUMBNAIL_COUNT_ARIA', {
                  itemsQuantity: `${list.items.length}`,
              });

    return (
        <div className={styles.wrapper}>
            <Button
                type="plain"
                data-testid="select-list-item"
                className={styles.container}
                onClick={() => (window.location.href = linkHref)}
                aria-label={`${translate('LISTOVERVIEW_LISTS_THUMBNAIL_ARIA', {
                    listName: list.name,
                })}, ${numberOfItems}, ${dateText}`}
            >
                <div className={styles.content}>
                    <Text
                        textStyle="Heading.S"
                        tagName="h2"
                        className={styles.listName}
                    >
                        {list.name}
                    </Text>
                    <Text
                        textStyle="Body.M"
                        tagName="span"
                        className={styles.caption}
                    >
                        {dateText}
                    </Text>
                </div>
                <ListThumbnailImages items={[...itemImages, ...designImages]} />
            </Button>
            <Button
                className={styles.settingsButton}
                size="small"
                iconOnly
                type="tertiary"
                ssrIcon={ellipsisIcon}
                aria-label={translate(
                    'LISTOVERVIEW_LISTS_THUMBNAIL_SETTINGS_ARIA',
                    {
                        listName: list.name,
                    }
                )}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    showModal('ListMenu', {
                        list,
                        isLandingPage: true,
                    });
                }}
            />
        </div>
    );
};

export default ListThumbnail;
